import { supabase } from '@/integrations/supabase/client';
import type {
  AgreementPaymentTerm,
  AgreementPaymentTermInsert,
  AgreementSLA,
  AgreementSLAInsert,
  AgreementDeliverable,
  AgreementDeliverableInsert,
  AgreementStatus,
} from '@/types/agreements';

export type AgreementStructuredTable =
  | 'agreement_deliverables'
  | 'agreement_payment_terms'
  | 'agreement_slas';

export type AgreementStructuredRowMap = {
  agreement_deliverables: AgreementDeliverable;
  agreement_payment_terms: AgreementPaymentTerm;
  agreement_slas: AgreementSLA;
};

export type AgreementStructuredInsertMap = {
  agreement_deliverables: AgreementDeliverableInsert;
  agreement_payment_terms: AgreementPaymentTermInsert;
  agreement_slas: AgreementSLAInsert;
};

export type AgreementListSummary = {
  total: number;
  draft: number;
  pending: number;
  executed: number;
  expired: number;
};

const PENDING_STATUSES: AgreementStatus[] = ['sent_to_client', 'client_viewed', 'client_signed'];

export function buildAgreementSummary(
  agreements: Array<{ status: AgreementStatus }>,
): AgreementListSummary {
  return {
    total: agreements.length,
    draft: agreements.filter((agreement) => agreement.status === 'draft').length,
    pending: agreements.filter((agreement) => PENDING_STATUSES.includes(agreement.status)).length,
    executed: agreements.filter((agreement) => agreement.status === 'fully_executed').length,
    expired: agreements.filter((agreement) => agreement.status === 'expired').length,
  };
}

export async function fetchAgreementStructuredRows<T extends AgreementStructuredTable>(
  table: T,
  agreementId: string,
): Promise<Array<AgreementStructuredRowMap[T]>> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('agreement_id', agreementId)
    .order('sort_order');

  if (error) throw error;
  return data as Array<AgreementStructuredRowMap[T]>;
}

export async function replaceAgreementStructuredRows<T extends AgreementStructuredTable>(
  table: T,
  agreementId: string,
  items: Array<AgreementStructuredInsertMap[T]>,
): Promise<void> {
  await supabase.from(table).delete().eq('agreement_id', agreementId);

  if (items.length === 0) {
    return;
  }

  const { error } = await supabase
    .from(table)
    .insert(items.map((item, index) => ({ ...item, sort_order: index })));

  if (error) throw error;
}
